/**
 * useDocumentTitle hook
 *
 * Sets the document title based on the current route and optionally
 * moves focus to the main content region after navigation.
 *
 * Format: "Page Title | Floline" (or just "Floline" for the home page).
 * Satisfies WCAG 2.4.2 (Page Titled).
 */
import { useEffect, useRef } from "react";
import { useLocation } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

/** Map pathname prefixes to i18n page-title keys (common:pageTitle.*) */
const ROUTE_TITLE_MAP: Array<{ prefix: string; key: string; exact?: boolean }> = [
  { prefix: "/ai-check-in", key: "pageTitle.aiCheckIn" },
  { prefix: "/check-in", key: "pageTitle.checkIn" },
  { prefix: "/daily/", key: "pageTitle.timeline" },
  { prefix: "/timeline", key: "pageTitle.timeline" },
  { prefix: "/events", key: "pageTitle.events" },
  { prefix: "/trends", key: "pageTitle.trends" },
  { prefix: "/settings", key: "pageTitle.settings" },
  { prefix: "/onboarding", key: "pageTitle.onboarding" },
  { prefix: "/data-access", key: "pageTitle.dataAccess" },
  { prefix: "/login", key: "pageTitle.login" },
  { prefix: "/register", key: "pageTitle.register" },
  { prefix: "/privacy", key: "pageTitle.privacy" },
  { prefix: "/terms", key: "pageTitle.terms" },
  { prefix: "/", key: "pageTitle.dashboard", exact: true },
];

/**
 * Resolve the i18n key for the current pathname.
 *
 * @internal Exported for testing only.
 */
export function resolvePageTitleKey(pathname: string): string {
  for (const route of ROUTE_TITLE_MAP) {
    if (route.exact ? pathname === route.prefix : pathname.startsWith(route.prefix)) {
      return route.key;
    }
  }
  return "pageTitle.default";
}

const APP_NAME = "Floline";

/**
 * Sets `document.title` on every pathname change and optionally moves
 * keyboard focus to the main content container so screen-reader users
 * hear the new page context (WCAG 2.4.3 Focus Order).
 */
export function useDocumentTitle() {
  const { pathname } = useLocation();
  const { t } = useTranslation("common");
  const prevPathnameRef = useRef<string | null>(null);

  useEffect(() => {
    const titleKey = resolvePageTitleKey(pathname);
    const pageTitle = t(titleKey);

    // Home page → just the app name; other pages → "Page | Floline"
    document.title =
      titleKey === "pageTitle.dashboard"
        ? APP_NAME
        : `${pageTitle} | ${APP_NAME}`;

    // Move focus to main content on route changes (not on first mount).
    const isNavigation = prevPathnameRef.current !== null && prevPathnameRef.current !== pathname;
    prevPathnameRef.current = pathname;

    if (isNavigation) {
      // Defer to allow the new route content to render first.
      requestAnimationFrame(() => {
        const mainEl = document.getElementById("main-content");
        if (mainEl) {
          mainEl.focus({ preventScroll: true });
        }
      });
    }
  }, [pathname, t]);
}
